import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { MarketplaceServiceCard } from "@/components/marketplace/service-card";
import { listMyFavorites } from "@/lib/marketplace/community.functions";
import type { MpServiceCard } from "@/lib/marketplace/queries";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/favorites")({
  head: () => ({
    meta: [
      { title: "Saved services | BeHot.Now Marketplace" },
      { name: "description", content: "The Marketplace services you saved on BeHot.Now." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: FavoritesPage,
});

function FavoritesPage() {
  const { t } = useT();
  const { user, loading } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["mp-favorites", user?.id ?? null],
    enabled: Boolean(user),
    queryFn: () => listMyFavorites({}),
  });

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-6xl px-4 py-10">
        <h1 className="font-display text-3xl font-bold">{t("mp.favorites.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("mp.favorites.subtitle")}
        </p>

        {!user && !loading && (
          <p className="mt-8 text-sm text-muted-foreground">
            <Link to="/login" className="underline">
              {t("mp.common.sign_in")}
            </Link>{" "}
            {t("mp.favorites.sign_in_suffix")}
          </p>
        )}

        {isLoading && <Skeleton className="mt-6 h-40 w-full" />}
        {data && data.length === 0 && <p className="mt-8 text-sm text-muted-foreground">{t("mp.favorites.empty")}</p>}

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(data ?? []).map((s) => (
            <MarketplaceServiceCard key={s.id} service={s as unknown as MpServiceCard} />
          ))}
        </div>
      </div>
    </MarketplaceGate>
  );
}
